// ARRL Field Day bonus objectives and scoring
// Reference: ARRL Field Day rules (https://www.arrl.org/field-day-rules)

// ARFD operating classes (transmitter count + class letter)
// Classes B-F are always single transmitter, so transmitter count is locked to 1 for those.
export const ARFD_CLASSES = [
  { value: 'A', label: 'A - Club/Group', sublabel: 'A - Club/Group (outdoors, emergency power optional)', multiTx: true },
  { value: 'B', label: 'B - Portable', sublabel: 'B - One or two person portable', multiTx: false },
  { value: 'C', label: 'C - Mobile', sublabel: 'C - Mobile', multiTx: false },
  { value: 'D', label: 'D - Home (commercial)', sublabel: 'D - Home station (commercial power)', multiTx: false },
  { value: 'E', label: 'E - Home (emerg. power)', sublabel: 'E - Home station (emergency power)', multiTx: false },
  { value: 'F', label: 'F - EOC', sublabel: 'F - Emergency Operations Center', multiTx: false }
];

// ARFD bonus objectives.
// type: 'multiplier' means it multiplies QSO points (not additive).
//       'flat' means a fixed point value added to the score.
//       'per_unit' means N points per qualifying unit (e.g. per youth operator).
export const ARFD_OBJECTIVES = [
  {
    id: 'emergency_power',
    name: '100% Emergency Power',
    description: 'Operate ALL transmitters 100% on emergency power (generator, battery, solar, etc.). All station equipment including computers must run on emergency power.',
    type: 'multiplier',
    value: 2,
    autoDetectable: false
  },
  {
    id: 'public_location',
    name: 'Public Location',
    description: 'Operate from a public place such as a park, shopping center, or other location open to the general public.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'public_info_table',
    name: 'Public Information Table',
    description: 'Set up a public information table staffed and available to explain amateur radio and Field Day to the public.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'formal_message',
    name: 'Formal Message to ARRL SM/SEC',
    description: 'Send a formal radiogram to your ARRL Section Manager or Section Emergency Coordinator via amateur radio.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'w1aw_bulletin',
    name: 'W1AW Field Day Bulletin',
    description: 'Copy the W1AW Field Day bulletin transmitted during the Field Day operating period.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'satellite',
    name: 'Satellite QSO',
    description: 'Successfully complete at least one two-way QSO via an amateur satellite during the Field Day period.',
    type: 'flat',
    value: 100,
    autoDetectable: true,
    detectFn: 'checkSatellite'
  },
  {
    id: 'natural_power',
    name: 'Natural/Alternative Power',
    description: 'Make a minimum of 5 QSOs using only solar, wind, or water power (no generators or commercial power).',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'media_publicity',
    name: 'Media Publicity',
    description: 'Contact and invite local media (TV, radio, newspaper) to cover your Field Day operation. Submit a copy of the coverage.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'elected_official',
    name: 'Elected Official Visit',
    description: 'An elected government official (city, county, state, or federal) visits your Field Day site.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'agency_official',
    name: 'Agency Official Visit',
    description: 'A served agency official (ARES, Red Cross, CERT, etc.) visits and is shown the Field Day operation.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'edu_activity',
    name: 'Educational Activity',
    description: 'Conduct a formal, planned educational activity (licensing class, Elmering session, demonstration) at your Field Day site.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'social_media',
    name: 'Social Media',
    description: 'Post about your Field Day operation on social media (Facebook, Twitter/X, Instagram, etc.) with the hashtag #ARRLFieldDay.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'safety_officer',
    name: 'Safety Officer',
    description: 'Designate a specific Safety Officer for the Field Day operation.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  },
  {
    id: 'youth_operators',
    name: 'Youth Operators (under 18)',
    description: 'Licensed operators under 18 years old participate. Worth 20 points per youth operator, up to 100 points (5 operators).',
    type: 'per_unit',
    value: 20,
    maxValue: 100,
    unit: 'youth operators',
    autoDetectable: false
  },
  {
    id: 'web_submission',
    name: 'Web Submission',
    description: 'Submit your Field Day log via the ARRL web submission system.',
    type: 'flat',
    value: 50,
    autoDetectable: false
  },
  {
    id: 'gota_station',
    name: 'GOTA Station',
    description: 'Operate a Get On The Air (GOTA) station for non-licensed or recently licensed operators. Points earned from GOTA contacts are added to your score.',
    type: 'flat',
    value: 100,
    autoDetectable: false
  }
];

export const ARFD_MODE_GROUPS = {
  // Phone = 1 pt/QSO. Includes all voice modes, including digital voice.
  phone: ['SSB', 'AM', 'FM', 'DMR', 'C4FM', 'DSTAR'],
  // CW = 2 pts/QSO
  cw: ['CW'],
  // Digital = 2 pts/QSO. Includes FT8/FT4 which are the dominant digital modes.
  digital: ['RTTY', 'PSK31', 'OLIVIA', 'PACKET', 'SSTV', 'ATV', 'JS8', 'FT8', 'FT4']
};

export const ARFD_BANDS = [
  { label: '160m', min: 1.8, max: 2.0 },
  { label: '80m', min: 3.5, max: 4.0 },
  { label: '40m', min: 7.0, max: 7.3 },
  { label: '30m', min: 10.1, max: 10.15 },
  { label: '20m', min: 14.0, max: 14.35 },
  { label: '17m', min: 18.068, max: 18.168 },
  { label: '15m', min: 21.0, max: 21.45 },
  { label: '12m', min: 24.89, max: 24.99 },
  { label: '10m', min: 28.0, max: 29.7 },
  { label: '6m', min: 50.0, max: 54.0 },
  { label: '2m', min: 144.0, max: 148.0 },
  { label: '1.25m', min: 222.0, max: 225.0 },
  { label: '70cm', min: 420.0, max: 450.0 },
  { label: 'SAT', min: 0, max: 0, satellite: true }
];

export function getArfdBandFromFreq(freq) {
  const f = parseFloat(freq);
  if (!f || f === 0) return 'SAT';
  for (const band of ARFD_BANDS) {
    if (band.satellite) continue;
    if (f >= band.min && f <= band.max) return band.label;
  }
  return 'Other';
}

export function getArfdModeGroup(mode) {
  const m = mode.toUpperCase();
  if (ARFD_MODE_GROUPS.cw.includes(m)) return 'cw';
  if (ARFD_MODE_GROUPS.digital.includes(m)) return 'digital';
  return 'phone';
}

// Calculate per-contact QSO points under ARFD rules.
// Phone: 1 pt, CW: 2 pts, Digital: 2 pts
export function getArfdQsoPoints(mode) {
  const group = getArfdModeGroup(mode);
  return group === 'phone' ? 1 : 2;
}

// Detect auto-detectable bonuses from the contacts array.
export function detectArfdObjectives(contacts) {
  const detected = {};
  const hasSatellite = contacts.some(c => {
    const mode = c.mode?.toUpperCase();
    const freq = parseFloat(c.frequency);
    return mode === 'SAT' || freq === 0 || getArfdBandFromFreq(c.frequency) === 'SAT';
  });
  detected.satellite = hasSatellite;
  return detected;
}

// Calculate the final ARFD score.
// Formula: (rawQsoPoints × powerMultiplier) + bonusPoints
// powerMultiplier = 2 if emergency_power bonus is completed, otherwise 1.
// youth_operators is per-unit: value * count, capped at maxValue.
export function calculateArfdScore(contacts, completedObjectives, youthCount = 0) {
  const rawQsoPoints = contacts.reduce((sum, c) => {
    return sum + getArfdQsoPoints(c.mode || 'SSB');
  }, 0);

  const powerMultiplier = completedObjectives['emergency_power'] ? 2 : 1;
  const multipliedQsoPoints = rawQsoPoints * powerMultiplier;

  let bonusPoints = 0;
  for (const obj of ARFD_OBJECTIVES) {
    if (!completedObjectives[obj.id]) continue;
    if (obj.type === 'multiplier') continue; // handled above as powerMultiplier
    if (obj.type === 'per_unit') {
      // youthCount is stored separately; value per unit, capped at maxValue
      const count = obj.id === 'youth_operators' ? (youthCount || 0) : 0;
      bonusPoints += Math.min(obj.value * count, obj.maxValue);
    } else {
      bonusPoints += obj.value;
    }
  }

  return {
    rawQsoPoints,
    powerMultiplier,
    multipliedQsoPoints,
    bonusPoints,
    finalScore: multipliedQsoPoints + bonusPoints
  };
}
